import React, { useState } from "react";
import { Activity, Info } from "lucide-react";
import { exportCalculatorPDF } from "../../lib/pdfExport";
import { SavePdfButton } from "./SavePdfButton";

// HbA1c conversion utilities
//   HbA1c % → mmol/mol (IFCC): (HbA1c % - 2.15) × 10.929
//   HbA1c % → eAG mmol/L (ADAG): 1.59 × HbA1c % - 2.59
function percentToMmolPerMol(p) {
  return Math.round((p - 2.15) * 10.929);
}
function percentToEagMmolPerL(p) {
  return Math.round(((1.59 * p) - 2.59) * 10) / 10;
}

const HBA1C_TABLE = [5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0, 9.5, 10.0, 11.0, 12.0];

export default function HbA1cCalc({ lang }) {
  const [percent, setPercent] = useState("7.0");
  const value = parseFloat(percent);
  const valid = !isNaN(value) && value >= 4 && value <= 18;

  const mmolMol = valid ? percentToMmolPerMol(value) : null;
  const eAG = valid ? percentToEagMmolPerL(value) : null;

  const target =
    !valid ? null
    : value < 5.7 ? { color: "text-teal-600", bg: "bg-teal-200/30", border: "border-teal-300", label: lang === "et" ? "Norm" : lang === "ru" ? "Норма" : "Normal" }
    : value < 6.5 ? { color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-300", label: lang === "et" ? "Prediabeet" : lang === "ru" ? "Преддиабет" : "Prediabetes" }
    : value < 7.5 ? { color: "text-sapphire-700", bg: "bg-sapphire-50", border: "border-sapphire-300", label: lang === "et" ? "Diabeet — hea kontroll" : lang === "ru" ? "Диабет — контроль хороший" : "Diabetes — well controlled" }
    : value < 9.0 ? { color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-300", label: lang === "et" ? "Diabeet — vajab parandust" : lang === "ru" ? "Диабет — требует коррекции" : "Diabetes — needs improvement" }
    : { color: "text-red-600", bg: "bg-red-50", border: "border-red-300", label: lang === "et" ? "Diabeet — väga kõrge" : lang === "ru" ? "Диабет — очень высокий" : "Diabetes — very high" };

  return (
    <section className="card-clinic !p-7" data-testid="hba1c-calculator">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-sapphire-50 text-sapphire-700 flex items-center justify-center">
          <Activity className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-surface-ink">
            {lang === "et" ? "HbA1c kalkulaator" : lang === "ru" ? "Калькулятор HbA1c" : "HbA1c calculator"}
          </h2>
          <p className="text-[13px] text-surface-inkSoft">
            {lang === "et"
              ? "Teisendus protsentide, mmol/mol ja keskmise veresuhkru vahel."
              : lang === "ru"
              ? "Преобразование между %, mmol/mol и средним уровнем глюкозы."
              : "Convert between %, mmol/mol and estimated average glucose."}
          </p>
        </div>
      </div>

      <label className="block text-[13px] font-medium text-surface-ink mb-2">
        {lang === "et" ? "Sisesta HbA1c väärtus (%)" : lang === "ru" ? "Введите HbA1c (%)" : "Enter HbA1c (%)"}
      </label>
      <input
        type="number"
        step="0.1"
        min="4"
        max="18"
        value={percent}
        onChange={(e) => setPercent(e.target.value)}
        className="input w-full md:w-48 text-[18px] font-semibold"
        data-testid="hba1c-input"
      />

      {valid && (
        <div className={`mt-5 rounded-xl border ${target.border} ${target.bg} p-4`} data-testid="hba1c-result">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-surface-inkSoft">HbA1c</div>
              <div className="text-2xl font-bold text-surface-ink">{value.toFixed(1)} <span className="text-base font-medium">%</span></div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-surface-inkSoft">mmol/mol (IFCC)</div>
              <div className="text-2xl font-bold text-surface-ink">{mmolMol}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-surface-inkSoft">
                {lang === "et" ? "Keskmine veresuhkur" : lang === "ru" ? "Средняя глюкоза" : "Average glucose"}
              </div>
              <div className="text-2xl font-bold text-surface-ink">{eAG} <span className="text-base font-medium">mmol/L</span></div>
            </div>
          </div>
          <div className={`mt-3 text-[14px] font-semibold ${target.color}`}>{target.label}</div>
        </div>
      )}

      {valid && (
        <SavePdfButton
          lang={lang}
          testId="hba1c-save-pdf"
          onClick={() =>
            exportCalculatorPDF({
              lang,
              title:
                lang === "et" ? "HbA1c kalkulaator" : lang === "ru" ? "Калькулятор HbA1c" : "HbA1c calculator",
              inputs: [
                { label: "HbA1c (%)", value: value.toFixed(1) },
              ],
              results: [
                { label: "HbA1c %", value: `${value.toFixed(1)} %` },
                { label: "mmol/mol (IFCC)", value: String(mmolMol) },
                {
                  label:
                    lang === "et"
                      ? "Keskmine veresuhkur"
                      : lang === "ru"
                      ? "Средняя глюкоза"
                      : "Average glucose",
                  value: `${eAG} mmol/L`,
                },
                {
                  label: lang === "et" ? "Hinnang" : lang === "ru" ? "Оценка" : "Status",
                  value: target.label,
                },
              ],
              note:
                lang === "et"
                  ? "HbA1c eesmärk on enamasti < 7%. Individuaalsed eesmärgid määrab arst sõltuvalt vanusest, tervisest ja diabeedi tüübist."
                  : lang === "ru"
                  ? "Целевой HbA1c обычно < 7%. Индивидуальные цели определяет врач."
                  : "HbA1c target is usually < 7%. Individual targets are set by your doctor.",
            })
          }
        />
      )}

      <h3 className="mt-7 text-[14px] font-semibold text-surface-ink mb-3">
        {lang === "et" ? "Teisendustabel" : lang === "ru" ? "Таблица преобразования" : "Conversion table"}
      </h3>
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-[13px] border-collapse">
          <thead>
            <tr className="border-b border-surface-outline/60">
              <th className="text-left px-3 py-2 text-surface-inkSoft font-medium">HbA1c %</th>
              <th className="text-left px-3 py-2 text-surface-inkSoft font-medium">mmol/mol</th>
              <th className="text-left px-3 py-2 text-surface-inkSoft font-medium">{lang === "et" ? "Keskm. glükoos (mmol/L)" : lang === "ru" ? "Средн. глюкоза (mmol/L)" : "Avg glucose (mmol/L)"}</th>
              <th className="text-left px-3 py-2 text-surface-inkSoft font-medium">{lang === "et" ? "Hinnang" : lang === "ru" ? "Оценка" : "Status"}</th>
            </tr>
          </thead>
          <tbody>
            {HBA1C_TABLE.map((p) => {
              const statuses =
                p < 5.7 ? { et: "Norm", ru: "Норма", en: "Normal" }
                : p < 6.5 ? { et: "Prediabeet", ru: "Преддиабет", en: "Prediabetes" }
                : p < 7.5 ? { et: "Diabeet (eesmärk)", ru: "Диабет (цель)", en: "Diabetes (target)" }
                : p < 9.0 ? { et: "Vajab parandust", ru: "Требует коррекции", en: "Needs improvement" }
                : { et: "Väga kõrge", ru: "Очень высокий", en: "Very high" };
              const status = statuses[lang] || statuses.et;
              return (
                <tr key={p} className="border-b border-surface-outline/30 hover:bg-surface-canvas">
                  <td className="px-3 py-2 font-semibold text-surface-ink">{p.toFixed(1)}</td>
                  <td className="px-3 py-2">{percentToMmolPerMol(p)}</td>
                  <td className="px-3 py-2">{percentToEagMmolPerL(p)}</td>
                  <td className="px-3 py-2 text-surface-inkSoft">{status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-teal-200/15 border border-teal-300/40 p-4 text-[14px] leading-relaxed text-surface-ink">
        <Info className="h-5 w-5 text-teal-500 mt-0.5 shrink-0" />
        <p>
          {lang === "et"
            ? "HbA1c eesmärk on enamasti < 7%. Individuaalsed eesmärgid määrab arst sõltuvalt sinu vanusest, tervisest ja diabeedi tüübist."
            : lang === "ru"
            ? "Цель HbA1c обычно < 7%. Индивидуальные цели определяет врач."
            : "HbA1c target is usually < 7%. Individual targets are set by your doctor."}
        </p>
      </div>
    </section>
  );
}
